import { IMPERSONATION_BANNER_CLOSED_KEY } from '@/components/ImpersonatingBanner/constants'
import { debugTrackingEvents } from '@/heap/debug/debugEvents'
import { useQueryParams } from '@/hooks/useQueryParams'
import { CognitoMonitoringEvents } from '@/monitoring/newRelic/CognitoMonitoringEvents'
import { PATHS } from '@/routes/PATHS'
import { usePremiumFeaturesAccountCreatedAt } from '@/stores/permissions/premiumFeatures'
import {
  IMPERSONATION_COOKIE_NAME,
  IMPERSONATION_COOKIE_SETTINGS,
  MARQII_COGNITO_CLIENT_ID,
  MARQII_COGNITO_COOKIE_SETTINGS,
} from '@/utils/constants/oauth'
import { type CognitoUser } from '@aws-amplify/auth'
import { useUnleashContext } from '@unleash/proxy-client-react'
import { Auth } from 'aws-amplify'
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useCookies } from 'react-cookie'
import { useLocation, useNavigate } from 'react-router'

interface ApiError {
  type: string
  message: string
}

export type ContextError = ApiError | TypeError

export interface UserAttributes {
  email: string
  name: string
  sub: string
  'custom:tenantId': string
  given_name: string
  family_name: string
  email_verified?: string
  updated_at?: string
  'custom:optInMarketing'?: string
}

interface CognitoUserExt extends CognitoUser {
  attributes: UserAttributes
}

export interface CognitoAuthContextType {
  user: CognitoUser | null
  userId: string
  userAttributes?: UserAttributes
  accountId: string
  accessToken: string
  isAuthenticatedPreviousValue: boolean
  isAuthenticated: boolean
  loading: boolean
  impersonation: {
    isImpersonating: boolean
  }
  error?: ApiError | TypeError
  login: (email: string, password: string) => void
  logout: () => void
  updateUserAttributes: (data: Partial<UserAttributes>) => Promise<void>
  changePassword: (currentPassword: string, newPassword: string) => Promise<string>
  forgotPassword: (email: string) => Promise<void>
  resetPassword: (email: string, code: string, password: string) => Promise<void>
  changeTemporaryPassword: (newPassword: string, optInMarketing?: boolean) => Promise<void>
  getAccessToken: () => Promise<string | undefined>
}

const CognitoAuthContext = createContext<CognitoAuthContextType>({} as CognitoAuthContextType)

const isProduction = process.env.NODE_ENV === 'production'

export function CognitoAuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<CognitoUserExt | null>(null)
  const [accountId, setAccountId] = useState('')
  const [isImpersonating, setIsImpersonating] = useState(false)
  const [originAccountId, setOriginAccountId] = useState('')
  const [userAttributes, setUserAttributes] = useState<UserAttributes>({} as UserAttributes)
  const [accessToken, setAccessToken] = useState('')
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [error, setError] = useState<ContextError>()
  const [loading, setLoading] = useState<boolean>(false)
  const [loadingInitial, setLoadingInitial] = useState<boolean>(true)
  const [_, __, removeCookie] = useCookies([IMPERSONATION_COOKIE_NAME, MARQII_COGNITO_CLIENT_ID])

  const isAuthenticatedPreviousValue = useRef(false)

  const navigate = useNavigate()
  const location = useLocation()

  const queryParams = useQueryParams()
  const redirectUri = queryParams.get('redirect_uri')

  const accountCreatedAt = usePremiumFeaturesAccountCreatedAt()

  function validateUser(user: CognitoUser) {
    const token = user.getSignInUserSession()?.getAccessToken()?.getJwtToken()
    if (token) {
      setAccessToken(token)
      setIsAuthenticated(!!token)
    }
  }

  function checkIsFirstTimeLogin(user: CognitoUserExt | null) {
    return user?.challengeName === 'NEW_PASSWORD_REQUIRED'
  }

  async function checkAndSetAuthenticatedUser() {
    try {
      const user: CognitoUserExt = await Auth.currentAuthenticatedUser()
      validateUser(user)
      setUser(user)
    } catch (err) {
      setError(err as ContextError)
      console.info('error on initial auth: ', err)
      window.heap.clearEventProperties()
      window.heap.addEventProperties({ loggedIn: 'false' })
      if (user !== null) {
        console.info('stale session detected, logging out')
        await logout()
      }
    } finally {
      setLoadingInitial(false)
    }
  }

  // look for active session once on provider mount
  useEffect(() => {
    void checkAndSetAuthenticatedUser()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!isAuthenticated) return

    const authPathsToList = Object.values(PATHS)
      .filter((path) => path.includes('/auth'))
      .map((p) => String(p))

    if (redirectUri) {
      void navigate(decodeURIComponent(redirectUri), { replace: true })
    } else if (authPathsToList.includes(location.pathname)) {
      void navigate(PATHS.homePage, { replace: true })
    }
  }, [isAuthenticated, navigate, redirectUri, location.pathname])

  const updateContext = useUnleashContext()

  const userId = useMemo(() => userAttributes.sub, [userAttributes.sub])

  useEffect(() => {
    const properties = {
      accountId,
      ...(accountCreatedAt ? { accountCreatedAt: accountCreatedAt.getTime().toString() } : {}),
    }
    void updateContext({ properties, userId })

    if (userId && accountId) {
      window.heap.identify(userId)
      window.heap.addUserProperties({ accountId: originAccountId })
      window.heap.clearEventProperties()
      window.heap.addEventProperties({ accountId, loggedIn: 'true' })
    }

    if (!window.newrelic) {
      if (isProduction) console.warn('New Relic not initialized.')
      return
    }

    if (userId && !accountId) {
      debugTrackingEvents.loggedUserWithoutAccountId(userId)
      window?.newrelic?.addPageAction(CognitoMonitoringEvents.loggedUserWithoutAccountId, {
        accountId,
        originAccountId,
        userId,
      })
    }
  }, [accountId, originAccountId, userId, updateContext, accountCreatedAt])

  // Flags the component loading state and posts the login
  // data to the server.
  // Finally, just signal the component that loading the
  // loading state is over.
  async function login(email: string, password: string) {
    setError(undefined)
    setLoading(true)
    try {
      const user: CognitoUserExt = await Auth.signIn(email, password)
      const requireNewPassword = checkIsFirstTimeLogin(user)

      if (requireNewPassword) {
        void navigate(PATHS.newPasswordPage, { replace: true })
      }

      validateUser(user)
      setUser(user)
    } catch (e) {
      setError(e as ContextError)
      console.error('Error on sign in:', e)
    } finally {
      setLoading(false)
    }
  }

  // Call the logout endpoint and then remove the user
  // from the state.
  async function logout() {
    try {
      removeCookie(IMPERSONATION_COOKIE_NAME, {
        ...IMPERSONATION_COOKIE_SETTINGS,
      })
      removeCookie(MARQII_COGNITO_CLIENT_ID, MARQII_COGNITO_COOKIE_SETTINGS)
      await Auth.signOut()

      localStorage.removeItem(IMPERSONATION_BANNER_CLOSED_KEY)
    } catch (e) {
      console.error('Error on sign out:', e)
    } finally {
      window.heap.clearEventProperties()
      window.heap.addEventProperties({ loggedIn: 'false' })
      setAccessToken('')

      isAuthenticatedPreviousValue.current = isAuthenticated

      setIsAuthenticated(false)
      setUser(null)
    }
  }

  async function forgotPassword(email: string) {
    setLoading(true)

    await Auth.forgotPassword(email)

    setLoading(false)
  }

  async function resetPassword(email: string, code: string, password: string) {
    setError(undefined)
    setLoading(true)

    try {
      await Auth.forgotPasswordSubmit(email, code, password)
      void navigate(PATHS.signInPage)
    } catch (e) {
      setError(e as ContextError)
      console.error('Error on reset:', e)
      throw e
    } finally {
      setLoading(false)
    }
  }

  async function changeTemporaryPassword(newPassword: string, optInMarketing?: boolean) {
    setError(undefined)
    setLoading(true)

    try {
      const attributes = optInMarketing ? { 'custom:optInMarketing': '1' } : undefined
      await Auth.completeNewPassword(user, newPassword, attributes)
      void navigate(PATHS.signInPage, { replace: true })
    } catch (e) {
      setError(e as ContextError)
      console.error('Error on change temporary password:', e)
      throw e
    } finally {
      setLoading(false)
    }
  }

  // log current session for testing
  async function getCurrentSession() {
    try {
      return Auth.currentSession()
    } catch (e) {
      console.error('Error on fetch currentSession', e)
    }
  }

  async function changePassword(currentPassword: string, newPassword: string) {
    return Auth.changePassword(user, currentPassword, newPassword)
  }

  async function updateUserAttributes(modifiedData: Partial<UserAttributes>) {
    try {
      await Auth.updateUserAttributes(user, modifiedData)
      await checkAndSetAuthenticatedUser()
    } catch (e) {
      console.error('Error on fetch currentSession', e)
    }
  }

  async function getAccessToken() {
    const session = await getCurrentSession()
    return session?.getAccessToken().getJwtToken()
  }

  useEffect(() => {
    const requireNewPassword = checkIsFirstTimeLogin(user)
    const payload = user?.getSignInUserSession()?.getAccessToken()?.decodePayload()

    if (user?.attributes && !requireNewPassword) {
      setUserAttributes(user.attributes)
      setOriginAccountId(user.attributes['custom:tenantId'])
    }

    if (payload && payload['cognito:groups'] && Array.isArray(payload['cognito:groups'])) {
      setAccountId(payload['cognito:groups'][0])

      setIsImpersonating(payload['cognito:groups'].length > 1)
    }
  }, [user])

  // Make the provider update only when it should.
  // We only want to force re-renders if the user,
  // loading or error states change.
  const memoedValue: CognitoAuthContextType = useMemo(
    () => ({
      accessToken,
      accountId,
      error,
      isAuthenticated,
      isAuthenticatedPreviousValue: isAuthenticatedPreviousValue.current,
      loading,
      user,
      userId,
      userAttributes,
      login,
      logout,
      changePassword,
      getCurrentSession,
      updateUserAttributes,
      forgotPassword,
      resetPassword,
      changeTemporaryPassword,
      impersonation: {
        isImpersonating,
      },
      getAccessToken,
    }),
    // ignoring exhaustive deps to only re-render children if 'important' state changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accessToken, accountId, error, isAuthenticated, loading, user, userId, userAttributes],
  )

  // We only want to render the underlying app after we
  // assert for the presence of a current user.
  return (
    <CognitoAuthContext.Provider value={memoedValue}>
      {!loadingInitial && children}
    </CognitoAuthContext.Provider>
  )
}

// Let's only export the `useAuth` hook instead of the context.
// We only want to use the hook directly and never the context component.
export function useCognitoAuth() {
  return useContext(CognitoAuthContext)
}
